import { getRailsClient } from "sj-ui-toolkit/src/client";
import type {
  AssignmentRowStatus,
  AssignmentStatusFilter,
} from "@/constants/assignmentsReport.constants";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AssignmentReportRow {
  email: string;
  firstName: string;
  lastName: string;
  name: string;
  timeSpent?: string | null;
  dueDate: string | null;
  completedAt: string | null;
  status?: AssignmentRowStatus | null;
  progress?: { passed: number; all: number } | null;
}

export interface AssignmentsReportResponse {
  assignments: AssignmentReportRow[];
  totalPages: number;
}

export interface AssignmentsReportFilterOptions {
  jobRole: string[];
  title: string[];
  manager: string[];
  managersEmail: string[];
  employeeNumber: string[];
  securityChampion: string[];
  department: string[];
  businessUnit: string[];
  team: string[];
  company: string[];
  country: string[];
  assignment: string[];
}

export interface AssignmentsReportParams {
  page: number;
  per_page: number;
  sortable_column?: string | null;
  order_type?: "asc" | "desc";
  query?: string | null;
  from?: number | null;
  to?: number | null;
  status?: AssignmentStatusFilter[];
  job_role?: string[];
  title?: string[];
  department?: string[];
  business_unit?: string[];
  country?: string[];
  company?: string[];
  manager?: string[];
  security_champion?: string[];
  team?: string[];
  managers_email?: string[];
  employee_number?: string[];
  assignment?: string[];
  archived?: string[];
}

// Shape of lazyState.filters for this report — dates become ISO strings after
// JSON round-trip through localStorage.
export interface AssignmentReportFilters {
  global?: { value: string | null };
  dateRange?: { value: [string | null, string | null] | null };
  status?: { value: AssignmentStatusFilter[] };
  jobRole?: { value: string[] };
  title?: { value: string[] };
  manager?: { value: string[] };
  managersEmail?: { value: string[] };
  employeeNumber?: { value: string[] };
  securityChampion?: { value: string[] };
  department?: { value: string[] };
  businessUnit?: { value: string[] };
  team?: { value: string[] };
  company?: { value: string[] };
  country?: { value: string[] };
  assignment?: { value: string[] };
  archived?: { value: string[] };
}

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

export const loadAssignmentsReportPage = async (
  params: AssignmentsReportParams
): Promise<AssignmentsReportResponse> => {
  const API = `/api/internal_api/v1/admin/reports/assignments`;
  const { data } = await getRailsClient().get<AssignmentsReportResponse>(API, { params });
  return data;
};

export const downloadAssignmentsReportCsv = async (
  params: Omit<AssignmentsReportParams, "page" | "per_page">
): Promise<string> => {
  const API = `/api/internal_api/v2/admin/reports/assignments/download`;
  const { data } = await getRailsClient().get<string>(API, { params });
  return data;
};

export const loadAssignmentsReportFilterOptions =
  async (): Promise<AssignmentsReportFilterOptions> => {
    const API = `/api/internal_api/v1/admin/reports/progress_filters_component`;
    const { data } = await getRailsClient().get<AssignmentsReportFilterOptions>(API);
    return data;
  };
